import React from 'react'
import { Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import { api } from '../lib/api'
import { extractErrorMessage } from '../utils/errorHandler'

// Helper to format date as "dd MMM yyyy"
const formatDate = (dateInput?: string | Date | null): string => {
  if (!dateInput) return '-'
  const d = new Date(dateInput)
  if (isNaN(d.getTime())) return '-'
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
  return d.toLocaleDateString('en-US', options)
}

interface Customer {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  createdAt: string | Date
  totalOrders?: number
  totalSpent?: number
}

export default function CustomersPage() {
  const navigate = useNavigate()
  const filterManager = useAdminFilters({ defaultLimit: 10 })
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  // Fetch customers using TanStack Query
  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', page, limit, debouncedSearch, filterManager.filters],
    queryFn: async () => {
      const response = await api.getCustomers(page, limit, debouncedSearch, filterManager.filters)
      return response.data
    },
    staleTime: 1000 * 60, // 1 minute
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })

  const customers: Customer[] = (data?.data || []) as Customer[]
  const pagination = data?.pagination || { total: 0, pages: 0 }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-[#6D3A0D]">Customers</h1>
      </div>

      <AdminFilters
        search={search}
        searchPlaceholder="Search customers by name, email, or phone..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={isLoading}
      />

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#6D3A0D]"></div>
        </div>
      ) 
      : error ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-red-200">
          <p className="text-red-600">{extractErrorMessage(error)}</p>
        </div>
      )
       : customers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-[#F6EEE7]">
          <p className="text-gray-600">
            {hasActiveFilters ? 'No customers match your search filters.' : 'No customers found.'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#6D3A0D] underline hover:text-[#5D2A0D]"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#F6EEE7] shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F6EEE7] border-b-2 border-[#F6EEE7]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Joined
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-[#6D3A0D] uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F6EEE7]">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-[#F6EEE7]/50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {customer.firstName} {customer.lastName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{customer.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{customer.phone || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {customer.createdAt
                      ? formatDate(customer.createdAt)
                      : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm flex gap-3">
                    <button
                      onClick={() => navigate(`/customers/${customer.id}`)}
                      className="text-[#6D3A0D] hover:text-[#5D2A0D] font-semibold flex items-center gap-1"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.total > 0 && (
        <div className="mt-6">
          <AdminPagination
            page={page}
            limit={limit}
            total={pagination.total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={isLoading}
            showInfo={true}
          />
        </div>
      )}
    </div>
  )
}
